import React from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * ThemeToggle - the sun/moon/system control.
 *
 * Three states, not two, because the default is system and a two-state switch
 * cannot express "follow my device". A single cycling button would fit a navbar
 * more tightly, but it hides the current state and forces a guess about what the
 * next press does, so this is a segmented group of three with the active one
 * marked by aria-pressed.
 *
 * Icons only, with the label in title and aria-label. The group carries a
 * role of group and a label of its own, so a screen reader announces what the
 * three buttons are for rather than three unrelated toggles.
 *
 * Styling lives in index.css under .theme-toggle, deliberately: this control
 * exists to serve theming, so it would be an odd place to bake in literal
 * colours that cannot follow the theme.
 */

const SunIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="4.2" />
    <g strokeLinecap="round">
      <line x1="12" y1="2.5" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="21.5" />
      <line x1="2.5" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="21.5" y2="12" />
      <line x1="5.4" y1="5.4" x2="7.2" y2="7.2" />
      <line x1="16.8" y1="16.8" x2="18.6" y2="18.6" />
      <line x1="5.4" y1="18.6" x2="7.2" y2="16.8" />
      <line x1="16.8" y1="7.2" x2="18.6" y2="5.4" />
    </g>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20.5 14.8A8.6 8.6 0 0 1 9.2 3.5a8.6 8.6 0 1 0 11.3 11.3z" />
  </svg>
);

/** A display, for "whatever this device is set to". */
const SystemIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect x="2.8" y="4" width="18.4" height="12.4" rx="1.8" />
    <line x1="8.5" y1="20" x2="15.5" y2="20" strokeLinecap="round" />
    <line x1="12" y1="16.4" x2="12" y2="20" />
  </svg>
);

const OPTIONS = [
  { mode: 'light', label: 'Light theme', short: 'Light', Icon: SunIcon },
  { mode: 'dark', label: 'Dark theme', short: 'Dark', Icon: MoonIcon },
  { mode: 'system', label: 'Match my device', short: 'System', Icon: SystemIcon }
];

/**
 * @param {string} className - extra classes for the wrapper, so a host header
 *   can adjust spacing without this component knowing where it sits.
 */
function ThemeToggle({ className = '' }) {
  const { mode, setTheme } = useTheme();

  return (
    <div
      className={`theme-toggle ${className}`.trim()}
      role="group"
      aria-label="Colour theme"
    >
      {OPTIONS.map((option) => {
        const isActive = mode === option.mode;
        const { Icon } = option;

        return (
          <button
            key={option.mode}
            type="button"
            className={`theme-toggle-option ${isActive ? 'active' : ''}`.trim()}
            onClick={() => setTheme(option.mode)}
            aria-pressed={isActive}
            aria-label={option.label}
            title={option.label}
          >
            <Icon />
            <span className="theme-toggle-text">{option.short}</span>
          </button>
        );
      })}
    </div>
  );
}

export default ThemeToggle;
